import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { io } from 'socket.io-client';
import { useTranslation } from 'react-i18next';
import { Phone, PhoneOff, Mic, MicOff, Loader2, AlertCircle, Copy, Check, ShieldCheck, Circle, GripVertical, Maximize2 } from 'lucide-react';
import api from '../../utils/api';
import useDraggable from '../../hooks/useDraggable';
import { RTC_CONFIG, CONNECT_TIMEOUT_MS, fetchAgentIceConfig } from '../../utils/iceConfig';

function formatTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

// Self-contained agent-side voice call. Mounts with a ticketId, initiates the call
// (which sends the WhatsApp link to the client), then answers the client's WebRTC
// offer and records the mixed audio (agent mic + client) to the server in chunks.
//
// `join` ({ id, mode }) = entrar a una sala que abrió el CLIENTE desde el chat embebido, en
// vez de crear una. El cliente ya está adentro esperando: se salta /initiate y el enlace.
export default function VoiceCallWidget({ ticketId, contactName, onClose, mode: modoPedido = 'audio', join = null }) {
  const mode = join?.mode || modoPedido;
  // 'video' pide cámara además del micrófono y muestra el remoto. Por defecto 'audio',
  // así todas las llamadas que ya existen se comportan exactamente igual que antes.
  // 'screen' (el cliente comparte su pantalla): el agente sólo pone el micrófono.
  const esVideo = mode === 'video';
  const [remotoConVideo, setRemotoConVideo] = useState(false);
  const muestraVideo = esVideo || mode === 'screen' || remotoConVideo;
  const { t } = useTranslation();
  const [state, setState] = useState('init'); // init | waiting | connecting | connected | ended | error
  const [error, setError] = useState('');
  const [link, setLink] = useState('');
  const [waSent, setWaSent] = useState(false);
  const [windowClosed, setWindowClosed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [reconnecting, setReconnecting] = useState(false);
  const [stalled, setStalled] = useState(false); // connecting too long / media failed — show honest retry
  // Floating, draggable window so the agent can keep working in the helpdesk during the call.
  const { pos, isDragging, handlers: dragHandlers, elementRef } = useDraggable('voice-call-widget-pos', { defaultZone: 'bottom-right', width: 340, height: 360 });

  const callRef = useRef(null); // { id, token }
  const socketRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);
  const timerRef = useRef(null);
  const pendingCandidates = useRef([]);
  const watchdogRef = useRef(null); // fires if we sit in connecting too long
  const iceRestartedRef = useRef(false); // only auto-restart ICE once per attempt
  const rtcConfigRef = useRef(RTC_CONFIG); // live ICE config (managed TURN) fetched on mount
  const remoteStreamRef = useRef(null);
  const recDestRef = useRef(null);

  // El <video> remoto puede aparecer después de que llega la pista (el cliente activó la
  // pantalla a mitad de la llamada): se engancha después de cada render.
  useEffect(() => {
    const v = remoteVideoRef.current;
    const s = remoteStreamRef.current;
    if (v && s && v.srcObject !== s) { v.srcObject = s; v.play().catch(() => {}); }
  });

  // Recording
  const audioCtxRef = useRef(null);
  const recorderRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const chunkQueueRef = useRef(Promise.resolve());
  const recordingStartedRef = useRef(false);

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch {}
    }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} audioCtxRef.current = null; }
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(tr => tr.stop()); localStreamRef.current = null; }
    if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
    recordingStartedRef.current = false;
  }, []);

  // ── Recording (mix agent mic + client audio) ──
  function uploadChunk(blob, callId, index) {
    const fd = new FormData();
    fd.append('chunk', blob, `chunk-${index}.webm`);
    fd.append('index', String(index));
    return api.post(`/voice-calls/calls/${callId}/chunk`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000,
    }).catch((err) => console.error('[VoiceCall] chunk upload failed:', err.message));
  }

  function startRecording(remoteStream) {
    if (recordingStartedRef.current) return;
    const callId = callRef.current?.id;
    const localStream = localStreamRef.current;
    if (!callId || !localStream || !remoteStream) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ac = new AC();
      audioCtxRef.current = ac;
      const dest = ac.createMediaStreamDestination();
      recDestRef.current = dest;
      try { ac.createMediaStreamSource(localStream).connect(dest); } catch {}
      try { ac.createMediaStreamSource(remoteStream).connect(dest); } catch {}

      const codecs = ['audio/webm;codecs=opus', 'audio/webm'];
      const mimeType = codecs.find(c => MediaRecorder.isTypeSupported(c));
      if (!mimeType) { console.warn('[VoiceCall] no supported recording codec'); return; }

      const recorder = new MediaRecorder(dest.stream, { mimeType, audioBitsPerSecond: 32000 });
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          const idx = chunkIndexRef.current++;
          const blob = e.data;
          chunkQueueRef.current = chunkQueueRef.current.then(() => uploadChunk(blob, callId, idx));
        }
      };
      recorder.onerror = (e) => console.error('[VoiceCall] recorder error:', e.error?.message || e);
      recorder.start(30000); // 30s chunks
      recordingStartedRef.current = true;
    } catch (err) {
      console.error('[VoiceCall] failed to start recording:', err.message);
    }
  }

  function stopRecordingAndFinalize() {
    const callId = callRef.current?.id;
    return new Promise((resolve) => {
      if (!recorderRef.current || recorderRef.current.state === 'inactive') { resolve(); return; }
      recorderRef.current.onstop = () => {
        if (callId) {
          chunkQueueRef.current.then(() => {
            api.post(`/voice-calls/calls/${callId}/recording/finalize`, {}, { timeout: 60000 }).catch(() => {});
          }).finally(resolve);
        } else resolve();
      };
      try { recorderRef.current.stop(); } catch { resolve(); }
      setTimeout(resolve, 15000);
    });
  }

  // Ask the client to send a fresh offer (used to recover a failed/stalled media path).
  function requestReoffer() {
    const token = callRef.current?.token;
    if (token && socketRef.current) socketRef.current.emit('request-reoffer', { token });
  }

  // Arm a watchdog: if we don't reach 'connected' within the timeout, first try one ICE
  // restart (ask the client to re-offer), and if that also fails, surface an honest
  // "couldn't connect" state instead of spinning forever.
  function armWatchdog() {
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    watchdogRef.current = setTimeout(() => {
      if (pcRef.current && pcRef.current.connectionState === 'connected') return;
      if (!iceRestartedRef.current) {
        iceRestartedRef.current = true;
        requestReoffer();
        armWatchdog(); // give the restart one more window
      } else {
        setReconnecting(false);
        setStalled(true);
      }
    }, CONNECT_TIMEOUT_MS);
  }

  // ── Answer the client's WebRTC offer ──
  async function handleOffer(offer, token, socket) {
    let pc = pcRef.current;
    if (pc) {
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit('answer', { token, answer });
        return;
      } catch {
        pc.close(); pcRef.current = null;
      }
    }

    pc = new RTCPeerConnection(rtcConfigRef.current);
    pcRef.current = pc;

    pc.ontrack = (e) => {
      const stream = e.streams[0];
      if (!stream) return;
      // Con video el <video> reproduce las dos pistas; enchufar también el <audio> al
      // mismo stream duplicaría el sonido. Se mira la pista y no el modo: el cliente puede
      // activar la pantalla a mitad de una llamada de voz.
      if (stream.getVideoTracks().length > 0) {
        remoteStreamRef.current = stream;
        if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
        setRemotoConVideo(true);
        const v = remoteVideoRef.current;
        if (v) { v.srcObject = stream; v.play().catch(() => {}); }
      } else if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = stream;
        remoteAudioRef.current.play().catch(() => {});
      }
      // Si el cliente cambió de medio (oferta nueva = stream nuevo), el audio nuevo se suma
      // a la grabación que ya corre; si no, la grabación se quedaba muda desde ese momento.
      if (recordingStartedRef.current && recDestRef.current && audioCtxRef.current && stream.getAudioTracks().length) {
        try { audioCtxRef.current.createMediaStreamSource(stream).connect(recDestRef.current); } catch {}
      }
      startRecording(stream);
    };

    pc.onicecandidate = (e) => {
      if (e.candidate) socket.emit('ice-candidate', { token, candidate: e.candidate });
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        setReconnecting(false);
        setStalled(false);
        iceRestartedRef.current = false;
        if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
        setState('connected');
        if (!timerRef.current) {
          const start = Date.now();
          timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
        }
      }
      // 'disconnected'/'failed' is usually transient (the client backgrounded their phone). Do NOT end
      // the call — show "reconnecting" and let signaling re-establish. The watchdog will try one ICE
      // restart and then surface an honest failure if the media path truly can't come up. Only an
      // explicit hang-up or the backend grace timeout (call-ended) actually ends it.
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        setReconnecting(true);
        armWatchdog();
      }
    };

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(tr => pc.addTrack(tr, localStreamRef.current));
    }

    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    for (const c of pendingCandidates.current) await pc.addIceCandidate(new RTCIceCandidate(c));
    pendingCandidates.current = [];

    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    socket.emit('answer', { token, answer });
  }

  function connectSocket(token) {
    const authToken = localStorage.getItem('accessToken');
    // Allow polling fallback: on WS-hostile networks the raw websocket upgrade can fail, and
    // websocket-only would leave signaling permanently dead. Polling always gets through.
    const socket = io('/voice-call', { auth: { token: authToken }, transports: ['websocket', 'polling'], reconnection: true });
    socketRef.current = socket;

    // Al entrar a una sala del cliente, él ya está adentro: lo que sigue es conectar, no esperar.
    socket.on('connect', () => { socket.emit('join-call', { token }); setState(s => (s === 'connected' ? s : (join ? 'connecting' : 'waiting'))); });
    socket.on('offer', async ({ offer }) => { setStalled(false); await handleOffer(offer, token, socket); });
    socket.on('ice-candidate', async ({ candidate }) => {
      if (pcRef.current && pcRef.current.remoteDescription) await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      else pendingCandidates.current.push(candidate);
    });
    socket.on('peer-joined', ({ type }) => { if (type === 'customer') { setReconnecting(false); setState(s => (s === 'connected' ? s : 'connecting')); armWatchdog(); } });
    // Customer dropped their socket (very common on phones when they background the browser). Do NOT
    // end the call — show "reconnecting" and wait. The backend holds the call open for a grace window.
    socket.on('peer-disconnected', ({ type }) => { if (type === 'customer') setReconnecting(true); });
    socket.on('peer-reconnected', ({ type }) => { if (type === 'customer') setReconnecting(false); });
    socket.on('call-ended', () => finishEnded());
    socket.on('connect_error', () => {});
  }

  // ── Initiate on mount ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = join
          ? await api.post(`/voice-calls/calls/${join.id}/join`)
          : await api.post('/voice-calls/initiate', { ticket_id: ticketId, mode });
        if (cancelled) return;
        callRef.current = { id: data.id, token: data.token };
        setLink(data.link || '');
        setWaSent(!!data.waSent);
        setWindowClosed(!!data.windowClosed);

        // Pull the live ICE config (managed TURN + self-hosted) before we might answer an offer.
        rtcConfigRef.current = await fetchAgentIceConfig(api);
        if (cancelled) return;

        // Get agent mic
        try {
          localStreamRef.current = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            ...(esVideo ? { video: { width: { ideal: 1280 }, height: { ideal: 720 } } } : {}),
          });
          if (esVideo && localVideoRef.current) {
            localVideoRef.current.srcObject = localStreamRef.current;
            localVideoRef.current.play().catch(() => {});
          }
        } catch {
          if (!cancelled) { setError(t('voiceCall.micError', 'No pudimos acceder a tu micrófono')); setState('error'); }
          return;
        }
        if (cancelled) return;
        connectSocket(data.token);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || t('voiceCall.initError', 'No se pudo iniciar la llamada'));
          setState('error');
        }
      }
    })();
    return () => { cancelled = true; cleanup(); };
  }, [ticketId]); // eslint-disable-line react-hooks/exhaustive-deps

  function finishEnded() {
    setState('ended');
    stopRecordingAndFinalize().finally(() => {
      if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
      if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(tr => tr.stop()); localStreamRef.current = null; }
      if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    });
  }

  function endCall() {
    const token = callRef.current?.token;
    if (token && socketRef.current) socketRef.current.emit('end-call', { token });
    finishEnded();
  }

  function retryConnect() {
    setStalled(false);
    setReconnecting(true);
    iceRestartedRef.current = false;
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    pendingCandidates.current = [];
    requestReoffer();
    armWatchdog();
  }

  function toggleMute() {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      const muted = !track.enabled;
      setIsMuted(muted);
      const token = callRef.current?.token;
      if (token) socketRef.current?.emit('mute-toggle', { token, muted });
    }
  }

  function copyLink() {
    if (!link) return;
    navigator.clipboard?.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  }

  const isLive = state === 'connected';
  const headerColor = isLive ? '#10b981' : 'var(--c-brand)';

  const modal = (
    <div
      ref={elementRef}
      style={{
        position: 'fixed', left: pos.x, top: pos.y, zIndex: 1300,
        // Más ancho cuando hay pantalla o video que leer; 340 era para una llamada de voz.
        width: muestraVideo ? 560 : 340, maxWidth: 'calc(100vw - 24px)',
        transition: isDragging ? 'none' : 'left .18s ease, top .18s ease',
      }}
    >
      <audio ref={remoteAudioRef} autoPlay playsInline />

      {muestraVideo && (
        <div style={{ position: 'relative', width: '100%', marginBottom: 10 }}>
          <video ref={remoteVideoRef} autoPlay playsInline
                 style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain', borderRadius: 10, background: '#000', display: 'block' }} />
          {/* Una pantalla compartida en 560 px no se lee: pantalla completa en un clic. */}
          <button className="btn btn-secondary btn-icon btn-sm" onClick={() => remoteVideoRef.current?.requestFullscreen?.()}
                  title={t('voiceCall.fullscreen')} style={{ position: 'absolute', top: 8, right: 8, opacity: 0.9 }}>
            <Maximize2 size={14} />
          </button>
          {esVideo && (
            <video ref={localVideoRef} autoPlay playsInline muted
                   style={{ position: 'absolute', right: 8, bottom: 8, width: 92, borderRadius: 6,
                            background: '#000', border: '2px solid rgba(255,255,255,.6)' }} />
          )}
        </div>
      )}
      <div className="card" style={{ margin: 0, width: '100%', padding: 0, overflow: 'hidden', boxShadow: '0 14px 44px rgba(0,0,0,0.4)' }}>
        {/* Draggable header — grab here to move the floating window anywhere on screen */}
        <div
          {...dragHandlers}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
            borderBottom: '1px solid var(--c-border)', userSelect: 'none', touchAction: 'none',
            cursor: isDragging ? 'grabbing' : 'grab', background: 'var(--c-surface)',
          }}
        >
          <div style={{ width: 30, height: 30, borderRadius: 8, background: headerColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Phone size={15} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: reconnecting ? 'var(--c-pending, #d97706)' : 'var(--c-text)', lineHeight: 1.2 }}>
              {reconnecting ? t('voiceCall.reconnecting', 'Reconectando…')
                : mode === 'screen' ? t('voiceCall.screenTitle')
                : esVideo ? t('voiceCall.videoTitle') : t('voiceCall.title', 'Llamada de voz')}
            </div>
            <div style={{ fontSize: 11, color: 'var(--c-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {contactName || t('voiceCall.client', 'Cliente')}
            </div>
          </div>
          <GripVertical size={16} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />
        </div>
        <div style={{ padding: 20, textAlign: 'center' }}>

        {state === 'init' && (
          <div style={{ padding: '20px 0' }}>
            <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: 'var(--c-brand)', margin: '0 auto 12px', display: 'block' }} />
            <p style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>{t('voiceCall.starting', 'Iniciando llamada...')}</p>
          </div>
        )}

        {(state === 'waiting' || state === 'connecting') && (
          <div style={{ padding: '8px 0' }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%', margin: '0 auto 16px',
              background: 'rgba(59,130,246,0.1)', border: '2px solid rgba(59,130,246,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'vcw-pulse 2s ease-in-out infinite',
            }}>
              <Phone size={26} style={{ color: 'var(--c-brand)' }} />
            </div>
            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--c-text)', marginBottom: 6 }}>
              {state === 'connecting'
                ? t('voiceCall.connecting', 'Conectando con el cliente...')
                : t('voiceCall.waitingClient', 'Esperando que el cliente toque el enlace...')}
            </p>

            {waSent && (
              <p style={{ fontSize: 12, color: 'var(--c-text-muted)', marginBottom: 14 }}>
                <Check size={12} style={{ verticalAlign: -2 }} /> {t('voiceCall.linkSent', 'Enlace enviado por WhatsApp')}
              </p>
            )}
            {windowClosed && (
              <p style={{ fontSize: 12, color: 'var(--c-pending, #d97706)', marginBottom: 10 }}>
                {t('voiceCall.windowClosed', 'La ventana de WhatsApp está cerrada. Compartí este enlace con el cliente:')}
              </p>
            )}
            {link && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
                <input className="form-input" readOnly value={link} style={{ flex: 1, fontSize: 12 }} onFocus={(e) => e.target.select()} />
                <button className="btn btn-secondary btn-sm" onClick={copyLink} title={t('common.copy', 'Copiar')}>
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            )}

            {stalled && (
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 12px', marginBottom: 12, textAlign: 'left' }}>
                <p style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--c-text)', marginBottom: 4 }}>
                  {t('voiceCall.stalledTitle', 'No pudimos establecer la llamada')}
                </p>
                <p style={{ fontSize: 11.5, color: 'var(--c-text-muted)', marginBottom: 10 }}>
                  {t('voiceCall.stalledBody', 'La red del cliente puede estar bloqueando la conexión. Probá de nuevo o llamalo por teléfono.')}
                </p>
                <button className="btn btn-secondary btn-sm" onClick={retryConnect} style={{ width: '100%' }}>
                  <Phone size={14} /> {t('voiceCall.retry', 'Reintentar')}
                </button>
              </div>
            )}

            <button className="btn btn-danger" onClick={endCall} style={{ width: '100%' }}>
              <PhoneOff size={16} /> {t('voiceCall.cancel', 'Cancelar')}
            </button>
          </div>
        )}

        {state === 'connected' && (
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, background: 'rgba(16,185,129,0.12)', color: '#10b981', fontSize: 12, fontWeight: 600, marginBottom: 16 }}>
              <Circle size={8} fill="#10b981" color="#10b981" /> {t('voiceCall.live', 'En llamada')}
            </div>
            <div style={{ fontSize: 34, fontWeight: 300, fontVariantNumeric: 'tabular-nums', color: 'var(--c-text)', marginBottom: 2 }}>{formatTime(elapsed)}</div>
            <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginBottom: 20 }}>{t('voiceCall.duration', 'Duración')}</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18 }}>
              <button
                className="btn btn-icon"
                onClick={toggleMute}
                title={isMuted ? t('voiceCall.unmute', 'Activar micrófono') : t('voiceCall.mute', 'Silenciar')}
                style={{ width: 52, height: 52, borderRadius: '50%', background: isMuted ? 'rgba(239,68,68,0.15)' : 'var(--c-surface-2)', color: isMuted ? '#ef4444' : 'var(--c-text)' }}
              >
                {isMuted ? <MicOff size={20} /> : <Mic size={20} />}
              </button>
              <button
                onClick={endCall}
                title={t('voiceCall.end', 'Finalizar')}
                style={{ width: 60, height: 60, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(239,68,68,0.3)' }}
              >
                <PhoneOff size={24} />
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11, color: 'var(--c-text-muted)', marginTop: 18 }}>
              <Circle size={8} fill="#ef4444" color="#ef4444" /> {t('voiceCall.recording', 'Grabando · solo admins pueden escuchar')}
            </div>
          </div>
        )}

        {state === 'ended' && (
          <div style={{ padding: '12px 0' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', margin: '0 auto 14px', background: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
              <ShieldCheck size={26} />
            </div>
            <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--c-text)', marginBottom: 6 }}>{t('voiceCall.ended', 'Llamada finalizada')}</p>
            <p style={{ fontSize: 13, color: 'var(--c-text-muted)', marginBottom: 18 }}>
              {elapsed > 0 ? `${t('voiceCall.duration', 'Duración')}: ${formatTime(elapsed)}` : ''}
            </p>
            <button className="btn btn-secondary" onClick={onClose} style={{ width: '100%' }}>{t('common.close', 'Cerrar')}</button>
          </div>
        )}

        {state === 'error' && (
          <div style={{ padding: '12px 0' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', margin: '0 auto 14px', background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
              <AlertCircle size={26} />
            </div>
            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--c-text)', marginBottom: 6 }}>{t('voiceCall.couldNotStart', 'No se pudo iniciar')}</p>
            <p style={{ fontSize: 13, color: 'var(--c-text-muted)', marginBottom: 18 }}>{error}</p>
            <button className="btn btn-secondary" onClick={onClose} style={{ width: '100%' }}>{t('common.close', 'Cerrar')}</button>
          </div>
        )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
